import moment from 'moment'
import { makeAxiosHttpClient } from 'src/main/factories/http/axios-http-client-factory-no-redirect'
import { PollingTaskStatus } from 'src/main/store-models/polling-status/types'

// ─── IndexedDB persistence ────────────────────────────────────────────────────

const IDB_NAME = 'export-blob-cache'
const IDB_STORE = 'blobs'

const openDb = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const req = indexedDB.open(IDB_NAME, 1)
  req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE)
  req.onsuccess = () => resolve(req.result)
  req.onerror = () => reject(req.error)
})

const idbSave = async (jobId: string, blob: Blob): Promise<void> => {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite')
    tx.objectStore(IDB_STORE).put(blob, jobId)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

const idbLoad = async (jobId: string): Promise<Blob | undefined> => {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly')
    const req = tx.objectStore(IDB_STORE).get(jobId)
    req.onsuccess = () => resolve(req.result as Blob | undefined)
    req.onerror = () => reject(req.error)
  })
}

// ─── In-memory layer (fast path within the same session) ─────────────────────

const memoryCache = new Map<string, Blob>()

// ─── Fetch from server → save to memory + IndexedDB ─────────────────────────

const fetchAndCacheBlob = async (jobId: string): Promise<void> => {
  const httpClient = makeAxiosHttpClient()
  const { statusCode, body } = await httpClient.request({
    url: `/meta/export/download/${jobId}`,
    method: 'get',
    responseType: 'arraybuffer',
  } as any)

  if (statusCode === 200) {
    const blob = new Blob([body as ArrayBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    memoryCache.set(jobId, blob)
    idbSave(jobId, blob)
  }
}

// ─── Trigger browser download from cached blob ───────────────────────────────

const triggerDownload = (blob: Blob): void => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `META_GRUPO_CLIENTE_${moment().format('YYYY-MM-DD')}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ─── Public: download (memory → IndexedDB → server, nesta ordem) ─────────────

export const downloadExportCustomerGoalDetalhe = async (jobId: string): Promise<void> => {
  const fromMemory = memoryCache.get(jobId)
  if (fromMemory) { triggerDownload(fromMemory); return }

  const fromIdb = await idbLoad(jobId).catch(() => undefined)
  if (fromIdb) { memoryCache.set(jobId, fromIdb); triggerDownload(fromIdb); return }

  await fetchAndCacheBlob(jobId)
  const blob = memoryCache.get(jobId)
  if (blob) triggerDownload(blob)
}

// ─── Status polling ──────────────────────────────────────────────────────────

export const checkExportCustomerGoalStDet = async (jobId: string): Promise<PollingTaskStatus> => {
  const httpClient = makeAxiosHttpClient()
  const { statusCode, body } = await httpClient.request({
    url: `/meta/export/status/${jobId}`,
    method: 'get',
  })

  if (statusCode !== 200) throw new Error('Erro ao verificar status do export')

  const {
    status, errorMessage, iniciadoEm, finalizadoEm, progress,
  } = body as {
    status: string
    errorMessage?: string
    iniciadoEm?: string | null
    finalizadoEm?: string | null
    progress?: number | null
  }

  if (status === 'CONCLUIDO') {
    fetchAndCacheBlob(jobId)
    const meta: Record<string, string> = { jobId }
    if (iniciadoEm) meta.iniciadoEm = iniciadoEm
    if (finalizadoEm) meta.finalizadoEm = finalizadoEm
    return { status: 'success', message: 'Export concluído com sucesso', meta }
  }

  if (status === 'ERRO') {
    const meta: Record<string, string> = {}
    if (iniciadoEm) meta.iniciadoEm = iniciadoEm
    meta.finalizadoEm = new Date().toISOString()
    return {
      status: 'error',
      message: 'Erro ao exportar',
      errors: errorMessage ? [errorMessage] : ['Ocorreu um erro ao gerar o export'],
      meta,
    }
  }

  const meta: Record<string, string> = {}
  if (iniciadoEm) meta.iniciadoEm = iniciadoEm
  if (progress != null) meta.Progresso = `${progress}%`

  return {
    status: 'loading',
    message: status === 'AGUARDANDO' ? 'Aguardando processamento' : 'Processando exportação',
    meta,
  }
}
